/**
 * Which language the booking portal speaks (Aperio preset only).
 *
 * Pure functions, no DOM: i18n/index.ts feeds them the URL, the stored
 * choice and the browser's languages, and tests call them directly.
 *
 * Order, first match wins (QA 2026-09-25 #6):
 *   1. `?lang=fr|es|en` (what the marketing site's CTAs pass), or `?locale=`;
 *   2. a `/fr/...` or `/es/...` path prefix (`/fr/book/location`);
 *   3. the visitor's own earlier choice (language switcher, or 1-2 on an
 *      earlier visit), kept in localStorage;
 *   4. the browser's languages (`navigator.languages`, else `.language`);
 *   5. English.
 */

export type PortalLang = 'en' | 'fr' | 'es';

export const PORTAL_LANGS: PortalLang[] = ['en', 'fr', 'es'];

/** localStorage key holding the visitor's explicit choice. */
export const LANG_STORAGE_KEY = 'aperio.lang';

export type LangSource = 'query' | 'path' | 'stored' | 'navigator' | 'default';

/** 'fr', 'FR', 'fr-CA', 'fr_CA' -> 'fr'; anything unsupported -> undefined. */
export function normaliseLang(value?: string | null): PortalLang | undefined {
  const code = String(value || '').trim().slice(0, 2).toLowerCase();
  return (PORTAL_LANGS as string[]).includes(code)
    ? code as PortalLang
    : undefined;
}

/**
 * `/fr/book/location` -> { lang: 'fr', rest: '/book/location' }. A path
 * without a language prefix comes back untouched with no `lang`. `/en/...`
 * is accepted too, so a hand-typed English prefix does not 404.
 */
export function splitLangPrefix(pathname: string): {
  lang?: PortalLang;
  rest: string;
} {
  const match = /^\/(en|fr|es)(?=\/|$)(.*)$/i.exec(pathname || '');
  if (!match) return { rest: pathname || '/' };
  return {
    lang: match[1].toLowerCase() as PortalLang,
    rest: match[2] || '/',
  };
}

export interface DetectInput {
  search?: string;
  pathname?: string;
  stored?: string | null;
  navigatorLanguages?: readonly string[] | null;
}

export function detectLanguage(input: DetectInput): {
  lang: PortalLang;
  source: LangSource;
} {
  let params: URLSearchParams | null = null;
  try {
    params = new URLSearchParams(input.search || '');
  } catch {
    params = null;
  }
  const fromQuery = normaliseLang(params?.get('lang'))
    || normaliseLang(params?.get('locale'));
  if (fromQuery) return { lang: fromQuery, source: 'query' };

  const fromPath = splitLangPrefix(input.pathname || '').lang;
  if (fromPath) return { lang: fromPath, source: 'path' };

  const fromStore = normaliseLang(input.stored);
  if (fromStore) return { lang: fromStore, source: 'stored' };

  const fromBrowser = (input.navigatorLanguages || [])
    .map((l) => normaliseLang(l))
    .find(Boolean);
  if (fromBrowser) return { lang: fromBrowser, source: 'navigator' };

  return { lang: 'en', source: 'default' };
}
